import React from 'react';
import clsx from 'clsx';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import {
  PickerOption,
  PropertyPicker,
} from '../../violation/linear/PropertyPicker';

/**
 * The picker chip, shaped as a form field (#665). Every dropdown in
 * front-porch opens the same popover: a filter box, the options, a check
 * on the current one, Headless UI's keyboard handling. That's
 * `PropertyPicker`, the picker the create modals' chips use. This gives it
 * a full-width, bordered trigger so it sits in a labelled form beside
 * Catalyst `Input`s.
 *
 * Use a bare `PropertyPicker` chip in a create modal's property row; use
 * this where a dropdown has a label above it.
 *
 * Replaces Catalyst `Select`, raw `<select>` and the `Dropdown`-as-a-field
 * pattern. The value is a string as it was from a <select>; convert at the
 * call site (`Number(v)`) where the field is numeric.
 *
 * **Accessible name.** A Catalyst `<Label>` cannot point at this (it is a
 * button, not a form control), so pass the label as `label`. The button is
 * then named "Account: Operating" and found by
 * `getByRole('button', { name: /^Account/ })` in tests.
 */
export type FieldPickerOption = PickerOption;

interface Props {
  options: FieldPickerOption[];
  value: string | null | undefined;
  onChange: (value: string) => void;
  /** The field's label, for the accessible name. */
  label: string;
  /** Shown when nothing is chosen. */
  placeholder?: string;
  /** Typed into the popover's filter box. */
  filterPlaceholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** `md` lines up with Catalyst Input; `sm` with the 13px inputClass. */
  size?: 'md' | 'sm';
  /** On the wrapper: widths such as `w-56`. Full width otherwise. */
  className?: string;
}

const sizes = {
  md: 'rounded-lg px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] text-base/6 sm:px-[calc(theme(spacing.3)-1px)] sm:py-[calc(theme(spacing[1.5])-1px)] sm:text-sm/6',
  sm: 'rounded-md px-2 py-1 text-[13px]',
};

export const FieldPicker: React.FC<Props> = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Choose…',
  filterPlaceholder,
  disabled,
  invalid,
  size = 'md',
  className,
}) => {
  const current = options.find((o) => o.value === value);
  return (
    <div className={className ?? 'w-full'}>
      <PropertyPicker
        unstyled
        options={options}
        value={value || null}
        onChange={(v) => v !== value && onChange(v)}
        placeholder={filterPlaceholder ?? `${label}…`}
        ariaLabel={`${label}: ${current ? current.label : 'none'}`}
        readOnly={disabled}
        wrapperClassName="block w-full"
        panelClassName="min-w-[var(--button-width)]"
        className={clsx(
          'flex w-full items-center justify-between gap-2 border bg-transparent text-left text-fg dark:bg-fg/5',
          'focus:outline-none data-[focus]:ring-2 data-[focus]:ring-accent',
          invalid
            ? 'border-danger'
            : 'border-line data-[hover]:border-line-strong',
          disabled && 'opacity-50',
          sizes[size]
        )}
        trigger={
          <>
            <span className="flex min-w-0 items-center gap-1.5 truncate">
              {current?.icon}
              {current ? (
                current.label
              ) : (
                <span className="text-fg-muted">{placeholder}</span>
              )}
            </span>
            <ChevronUpDownIcon
              className="h-4 w-4 shrink-0 text-fg-muted"
              aria-hidden
            />
          </>
        }
      />
    </div>
  );
};
